import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  type Theme,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import { update } from 'ramda'
import {
  type NamedEntity,
  type Question,
  type QuestionAnswer,
  QuestionContent,
  QuestionView,
  QuestionViewNew,
} from '@pbt/pbt-ui-components'

import Features from '../../constants/features'
import {
  getAnswerInputTypes,
  getFeatureToggle,
} from '../../store/duck/constants'
import {
  fetchQuestionsLog,
  getQuestionsIsLoading,
  getQuestionsIsSaving,
  getQuestionsLog,
  saveQuestionsLog,
} from '../../store/duck/questions'
import { getCurrentAppointment } from '../../store/duck/schedules'
import { type Appointment } from '../../types/entities/appointments'
// @ts-ignore
import { useNavigateWithQueryString } from '../../utils'
import useCallbackWhenLoaded from '../../utils/useCallbackWhenLoaded'
import KioskScreen from './KioskScreen'

const StyledGrid = styled(Grid)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(4, 5, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
  '& p': {
    marginBottom: 0,
  },
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.colors.grayGray2,
  fontWeight: 500,
  fontSize: '1.4rem',
}))

const useStyles = makeStyles(
  (theme) => ({
    questionViewRoot: {
      marginTop: theme.spacing(7),
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(2),
      },
      '& p': {
        marginBottom: 0,
      },
    },
    questionViewText: {
      color: theme.colors.brandPrimary,
      fontSize: '2.6rem',
      fontWeight: 500,
    },
    questionViewRadioLabel: {
      fontSize: '1.8rem',
    },
  }),
  { name: 'QuestionsScreen' },
)

const QuestionsScreen = () => {
  const classes = useStyles()
  const muiTheme = useTheme()
  const dispatch = useDispatch()
  const navigateWithQueryString = useNavigateWithQueryString()
  const { t } = useTranslation('Common')

  const questionsList: Question[] = useSelector(getQuestionsLog)
  const isLoading: boolean = useSelector(getQuestionsIsLoading)
  const appointment = useSelector(getCurrentAppointment) as Appointment | null
  const AnswerInputTypes: NamedEntity[] = useSelector(getAnswerInputTypes)
  const isSaving: boolean = useSelector(getQuestionsIsSaving)
  const isCvcIntakeQuestionnaireEnabled = useSelector(
    getFeatureToggle(Features.CVC_INTAKE_QUESTIONNAIRE),
  )
  const isFollowUpQuestionsEnabled = useSelector(
    getFeatureToggle(Features.FOLLOW_UP_QUESTIONS),
  )
  const isConsentFormsEnabled = useSelector(
    getFeatureToggle(Features.DISPLAY_CONSENT_FORMS_ON_KIOSK),
  )

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0)
  const [questions, setQuestions] = useState<Question[]>(questionsList)

  const appointmentId = appointment?.id || ''

  const currentQuestion = questions[currentQuestionIndex]
  const questionAnswered = (currentQuestion?.answers || []).some(
    (answer: QuestionAnswer) => answer.selected,
  )
  const isFirstQuestion = currentQuestionIndex === 0

  const navigateToNextStep = useCallback(() => {
    navigateWithQueryString({
      url: isConsentFormsEnabled ? '/consent-forms' : '/documents',
    })
  }, [isConsentFormsEnabled, navigateWithQueryString])

  const hasQuestions = questionsList.length > 0

  const onLogSaved = useCallback(() => {
    navigateToNextStep()
  }, [navigateToNextStep])

  const onLogLoaded = useCallback(() => {
    if (!hasQuestions) {
      navigateToNextStep()
    }
  }, [hasQuestions, navigateToNextStep])

  const callbackWhenLoaded = useCallbackWhenLoaded(
    onLogLoaded,
    getQuestionsIsLoading,
  )

  const callbackWhenAllSaved = useCallbackWhenLoaded(
    onLogSaved,
    getQuestionsIsSaving,
  )

  const onQuestionNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    } else {
      onLogSaved()
    }
  }

  const callBackWhenQuestionSaved = useCallbackWhenLoaded(
    onQuestionNext,
    getQuestionsIsSaving,
  )

  const onSaveQuestion = useCallback(
    (newQuestionLog: Question[]) => {
      if (appointmentId) {
        dispatch(saveQuestionsLog({ appointmentId, log: newQuestionLog }))
      }
    },
    [appointmentId],
  )

  useEffect(() => {
    callbackWhenLoaded()
    dispatch(fetchQuestionsLog({ appointmentId }))
  }, [appointmentId])

  useEffect(() => {
    setQuestions(questionsList)
  }, [questionsList])

  const onBack = () => {
    setCurrentQuestionIndex(currentQuestionIndex - 1)
  }

  const onProceed = () => {
    if (isFollowUpQuestionsEnabled) {
      callBackWhenQuestionSaved()
      onSaveQuestion([currentQuestion])
    } else if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    } else {
      callbackWhenAllSaved()
      onSaveQuestion(questions)
    }
  }

  const onQuestionAnswered = (newQuestion: Question) => {
    const oldQuestionIndex = questions.findIndex(
      (question: Question) => question.id === newQuestion.id,
    )
    const newQuestions = update(oldQuestionIndex, newQuestion, questions)

    setQuestions(newQuestions)
  }

  return (
    <KioskScreen
      hideBackButton={isFirstQuestion}
      justifyContent="flex-start"
      proceedButtonDisabled={!questionAnswered}
      proceedButtonLabel={t('Common:NEXT_ACTION')}
      proceedButtonLoading={isSaving}
      onBack={isFirstQuestion ? undefined : onBack}
      onProceed={onProceed}
    >
      <StyledGrid
        container
        item
        alignItems={isLoading ? 'center' : 'stretch'}
        direction="column"
        justifyContent={isLoading ? 'center' : 'flex-start'}
      >
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <StyledTypography align={isMobile ? 'left' : 'center'}>
              {`${t('Common:QUESTION')} ${currentQuestionIndex + 1}/${
                questions.length
              }`}
            </StyledTypography>
            {isCvcIntakeQuestionnaireEnabled ? (
              <Grid item pt={7}>
                <QuestionViewNew
                  AnswerInputTypes={AnswerInputTypes}
                  question={currentQuestion}
                  renderQuestionContent={(props: any) => (
                    <QuestionContent
                      {...props}
                      questionTextStyles={{
                        color: muiTheme.colors.brandSecondary,
                        fontSize: '2.6rem',
                        fontWeight: 500,
                      }}
                    />
                  )}
                  showFollowUpQuestion={isFollowUpQuestionsEnabled}
                  onAnswered={onQuestionAnswered}
                />
              </Grid>
            ) : (
              <QuestionView
                AnswerInputTypes={AnswerInputTypes}
                classes={{
                  root: classes.questionViewRoot,
                  text: classes.questionViewText,
                  radioLabel: classes.questionViewRadioLabel,
                }}
                question={currentQuestion}
                onAnswered={onQuestionAnswered}
              />
            )}
          </>
        )}
      </StyledGrid>
    </KioskScreen>
  )
}

export default QuestionsScreen
